import { RefObject, useEffect, useState } from 'react'
import {
  useFieldArray,
  UseFieldArrayReturn,
  UseFormReturn,
  useWatch,
} from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../../../contexts/AuthContext'
import {
  PartisanChartFormValues,
  usePartisanChartForm,
} from '../../../../forms/charts/chart-partisan-form'
import { ChartResponse, ChartTypeEnum } from '../../../../models/chart'
import { State } from '../../../../models/state'
import { RoutePaths } from '../../../../routes'
import { ChartsService } from '../../../../services/charts-service'
import { PartiesService } from '../../../../services/parties-service'

export interface ManagePartisanChartState {
  loading: boolean
  mode: string
  form: UseFormReturn<PartisanChartFormValues>
  partiesFieldArray: UseFieldArrayReturn<PartisanChartFormValues, 'parties'>
  racesFieldArray: UseFieldArrayReturn<PartisanChartFormValues, 'races'>
  raceTypes: State[]
  saveChartDialog: boolean
  createChartDialog: boolean
  state?: State
  chart?: ChartResponse

  openSaveChartDialog: () => void
  closeSaveChartDialog: () => void
  openCreateChartDialog: () => void
  closeCreateChartDialog: () => void
  addNewRace: () => void
  removeRace: (index: number) => void
  moveRace: (dragIndex: number, hoverIndex: number) => void
  moveParty: (dragIndex: number, hoverIndex: number) => void
  sortRaces: () => void
  cancel: () => void
  saveChart: () => void
  createAnotherChart: (action: 'duplicate' | 'new') => void
  finishAndInvoice: () => void
  addPoliticalParty: (name: string, abbreviation: string) => void
  changeRaceType: (
    name: string,
    raceType: string,
    type: string,
    index: number
  ) => void
  addAllPartisanRaces(
    name: string,
    dialogRef: RefObject<HTMLDialogElement>
  ): void
}

const useManagePartisanChart = (
  chartType: ChartTypeEnum
): ManagePartisanChartState => {
  const [loading, setLoading] = useState<boolean>(true)
  const [mode, setMode] = useState<string>('create')
  const [chartId, setChartId] = useState<string>('')
  const [raceTypes, setRaceTypes] = useState<State[]>([])
  const [state, setState] = useState<State | undefined>()
  const [saveChartDialog, setSaveChartDialog] =
    useState<boolean>(false)
  const [chart, setChart] = useState<ChartResponse | undefined>(undefined)
  const [createChartDialog, setCreateChartDialog] =
    useState<boolean>(false);
  const params = useParams<{ chartId: string }>()
  const { account } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const form = usePartisanChartForm()

  const partiesFieldArray = useFieldArray({
    control: form.control,
    name: 'parties',
  })

  const formParties = useWatch({ control: form.control, name: 'parties' })

  const racesFieldArray = useFieldArray({
    control: form.control,
    name: 'races',
  })

  useEffect(() => {
    ; (async () => {
      const _raceTypes = await ChartsService.getRaceTypes()
      const parties = await PartiesService.getParties()
      setRaceTypes(_raceTypes)

      if (params.chartId) {
        setMode('edit')
        const isEditing = !location.pathname.includes('duplicate')
        setChartId(params.chartId)
        const chart = await ChartsService.getChart(params.chartId)
        setChart(chart)
        const selectedPoliticalParties = [
          ...chart.politicalParties.map((party, index) => ({
            id: index,
            name: party.name,
            abbv: party.abbv,
            selected: true,
          })),
        ]
        const allOtherParties = [
          ...parties
            .filter(
              (party) =>
                !selectedPoliticalParties.find((_) => _.name === party.name)
            )
            .map((party) => ({
              id: party.id,
              name: party.name,
              abbv: party.abbv,
              selected:
                chart.politicalParties.findIndex((_) => _.name === party.name) >
                -1,
            })),
        ]
        form.reset({
          municipality: chart.municipality,
          county: chart.county,
          precinct: isEditing ? chart.precinct : '',
          electionDate: getDateCleanedForLoading(chart.electionDate),
          showCandidateNames: chart.showCandidateNames,
          createPreferredParty: chart.createStraightParty,
          parties: [...selectedPoliticalParties, ...allOtherParties],

          races: chart.races.map((race) => ({
            id: race.id,
            name: race.name,
            type: race.type,
            raceType:
              race.raceType.name !== 'Partisan'
                ? race.raceType.name
                : race.politicalParties[0].name, // if it's partisan, we need to set the raceType to the party name
            candidates: race.candidates,
            candidateNames: race.candidateNames.map((item) => ({
              name: item.name,
              order: +item.order,
            })),
            voteFor: race.voteFor,
            writeIns: race.writeIns,

            parties: race.politicalParties.map((party) => ({
              abbv: party.abbv,

              candidateNames: party.candidateNames,

              candidates: party.candidates,

              id: party.id,

              name: party.name,
            })),
          })),
        })
      } else {
        setMode('create')
        form.reset({
          parties: parties.map((party) => ({
            id: party.id,
            name: party.name,
            abbv: party.abbv,
            selected: false,
          })),
        })
      }

      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (account && account.state) {
      setState(account.state)
    }
  }, [account])

  const onSubmit = async (values: PartisanChartFormValues): Promise<string> => {
    const selectedParties = values.parties.filter((_) => _.selected)
    const isEditing = !location.pathname.includes('duplicate') && chartId
    const payload = {
      ...(isEditing ? { id: chartId } : {}),
      adminchart: false,
      county: values.county.includes('/')
        ? values.county.replace('/', '-')
        : values.county.includes('\\')
          ? values.county.replace('\\', '-')
          : values.county,
      electionTypeId: chartType,
      electiondate: `${values.electionDate}T05:00:00Z`,
      labelsonright: false,
      municipality: values.municipality.includes('/')
        ? values.municipality.replace('/', '-')
        : values.municipality.includes('\\')
          ? values.municipality.replace('\\', '-')
          : values.municipality,
      precinct: values.precinct.includes('/')
        ? values.precinct.replace('/', '-')
        : values.precinct.includes('\\')
          ? values.precinct.replace('\\', '-')
          : values.precinct,
      stateId: account!.state.id,
      ...(isEditing ? {} : { userId: account!.id as string }),
      showCandidateNames: values.showCandidateNames,
      createStraightParty: values.createPreferredParty,

      politicalParties: selectedParties.map((party, idx) => ({
        ...(isEditing ? { id: party!.id as number } : {}),
        name: party.name,
        abbv: party.abbv,
        order: idx,
      })),

      races: values.races.map((race, raceIdx) => {
        return {
          ...(isEditing ? { id: race!.id as string } : {}),
          candidates:
            race.type.toLowerCase() === 'proposal'
              ? 0
              : race.candidates < 0
                ? 0
                : race.candidates,
          candidateNames: race.candidateNames
            ? values.showCandidateNames
              ? race.candidateNames
                .map((item) => ({ ...item, order: +item.order }))
                .filter(Boolean)
              : []
            : [],
          marks: [],
          name: race.name,
          order: raceIdx,
          type: race.type,
          raceTypeId:
            race.type.toLowerCase() === 'proposal'
              ? 1
              : race.raceType.toLowerCase() === 'non-partisan'
                ? 1
                : 0,
          voteFor: race.type.toLowerCase() === 'proposal' ? 1 : race.voteFor,
          writeIns: race.type.toLowerCase() === 'proposal' ? 0 : race.writeIns,

          politicalParties:
            race.type.toLowerCase() === 'proposal' ||
              race.type.toLowerCase() === 'non-partisan'
              ? []
              : selectedParties.filter((p) => p.name === race.raceType).length >
                0
                ? race.parties.map((party, idx) => ({
                  ...(isEditing ? { id: race?.parties[idx]?.id } : {}),
                  abbv: party.abbv,
                  candidates: race?.parties[idx]?.candidates || 0,
                  candidateNames: party.candidateNames
                    ? values.showCandidateNames
                      ? party.candidateNames
                        .map((item) => ({ ...item, order: +item.order }))
                        .filter(Boolean)
                      : []
                    : [],
                  name: party.name,
                  order: idx,
                }))
                : [],
          raceType: race.raceType === 'Non-Partisan' ? 1 : 0,
        }
      }),
    }

    return (
      isEditing
        ? // @ts-ignore
        await ChartsService.editChart(payload)
        : // @ts-ignore
        await ChartsService.createChart(payload)
    ).id
  }

  const addAllPartisanRaces = (
    name: string,
    dialogRef: RefObject<HTMLDialogElement>
  ) => {
    formParties
      .filter((p) => p.selected === true)
      .forEach((p) => {
        // add a new race for this party
        racesFieldArray.append({
          name: name,
          voteFor: 1,
          writeIns: 1,
          candidates: 0,
          candidateNames: [],
          parties: [
            {
              ...p,
              candidates: 0,
              candidateNames: [],
              id: p.id ? p.id.toString() : undefined,
            },
          ],
          type: 'Race',
          raceType: p.name,
        })
      })

    dialogRef.current?.close()
  }

  const addNewRace = () => {
    form.clearErrors('races')
    const lastRace = form.getValues().races
      ? form.getValues().races[
      form.getValues().races ? form.getValues().races.length - 1 : 0
      ]
      : undefined
    racesFieldArray.append({
      name: '',
      voteFor: 1,
      writeIns: 1,
      candidates: 0,
      candidateNames: [],
      parties: [
        {
          ...formParties[0],
          candidateNames: [],
          candidates: 0,
          id: formParties[0].id ? formParties[0].id.toString() : undefined,
        },
      ],
      type: lastRace ? lastRace.type : 'Race',
      raceType: lastRace ? lastRace.raceType : 'Non-Partisan',
    })
  }

  const changeRaceType = (
    name: string,
    raceType: string,
    type: string,
    index: number
  ) => {
    const party = formParties.find((item) => item.name === raceType)
    racesFieldArray.update(index, {
      name: name,
      voteFor: 1,
      writeIns: 1,
      candidates: 0,
      candidateNames: [],
      parties: party
        ? [
          {
            ...party,
            candidateNames: [],
            candidates: 0,
            id: party.id ? party.id.toString() : undefined,
          },
        ]
        : [],
      type: type,
      raceType: raceType,
    })
  }

  const removeRace = (index: number) => racesFieldArray.remove(index)

  const moveRace = (dragIndex: number, hoverIndex: number) => {
    const { races } = form.getValues()
    const dragCard = races[dragIndex]
    races.splice(dragIndex, 1)
    races.splice(hoverIndex, 0, dragCard)
    form.setValue('races', [...races])
  }

  const sortRaces = () => {
    const { races, parties } = form.getValues()

    const order: { [key: string]: number } = parties.reduce(
      (acc, party, i) => ({ ...acc, [party.name]: i }),
      { 'Non-Partisan': 25 }
    )

    const sortedRaces = races
      .filter((race) => race.type === 'Race')
      .sort((a, b) => {
        if (order[a.raceType] > order[b.raceType]) {
          return 1
        } else if (order[a.raceType] < order[b.raceType]) {
          return -1
        } else {
          return 0
        }
      })

    const proposals = races.filter((race) => race.type === 'Proposal')

    form.setValue('races', [...sortedRaces, ...proposals])
  }

  const addPoliticalParty = (partyName: string, partyAbbreviation: string) => {
    partiesFieldArray.append({
      name: partyName,
      abbv: partyAbbreviation,
      selected: true,
    })
  }

  const moveParty = (dragIndex: number, hoverIndex: number) => {
    const { parties } = form.getValues()
    const dragCard = parties[dragIndex]
    parties.splice(dragIndex, 1)
    parties.splice(hoverIndex, 0, dragCard)
    form.setValue('parties', [...parties])
  }

  const cancel = () => {
    navigate(RoutePaths.PARTISAN_CHARTS)
  }

  const saveChart = async () => {
    await toast.promise(
      form.handleSubmit(values => {
        onSubmit(values).then((createdChartId) => {
          setChartId(createdChartId)
          navigate(`/partisan/edit-chart/${createdChartId}`)
        })
      }, () => {
        // eslint-disable-next-line no-throw-literal
        throw 'Form is not valid'
      })(),
      {
        loading: 'Saving...',
        success: () => {
          // open the save chart dialog
          setSaveChartDialog(true)
          return <b>Successful chart save!</b>
        },
        error: (e) => {
          return e?.response?.data
            ? e.response.data
            : e?.message ?? e ?? 'An Error Occurred.'
        },
      },
      { duration: 5000 }
    )
  }

  const resetEditForm = () => {
    form.reset()
    form.setValue('county', '')
    form.setValue('electionDate', '')
    form.setValue('municipality', '')
    form.setValue('precinct', '')
    form.setValue('showCandidateNames', false)
    let { parties } = form.getValues()
    for (let i = 0; i < parties.length; i++) {
      parties[i].selected = true
      form.setValue(`parties.${i}`, parties[i])
    }
    racesFieldArray.remove()
    setChartId('')
  }

  const createAnotherChart = async (action: 'duplicate' | 'new') => {
    if (action === 'new') {
      resetEditForm()
      navigate(RoutePaths.CREATE_PARTISAN_CHART)

    } else if (chartId || params.chartId) {
      navigate('/partisan/duplicate-chart/' + chartId)
    }
    // close the create chart dialog
    setCreateChartDialog(false);
  }

  const finishAndInvoice = async () => {
    navigate(`/review-invoice/${ChartTypeEnum.PARTISAN_PRIMARY}/${form.getValues().electionDate}`)
  }

  const getDateCleanedForLoading = (date: string): string => {
    const d = new Date(date)
    const temp = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1 < 10
      ? '0' + (d.getUTCMonth() + 1)
      : d.getUTCMonth() + 1
      }-${d.getUTCDate() < 10 ? '0' + d.getUTCDate() : d.getUTCDate()}`
    return temp
  }

  return {
    mode,
    loading,
    form,
    raceTypes,
    partiesFieldArray,
    racesFieldArray,
    saveChartDialog,
    chart,
    createChartDialog,

    closeSaveChartDialog: () => setSaveChartDialog(false),
    openSaveChartDialog: () => setSaveChartDialog(true),
    closeCreateChartDialog: () => setCreateChartDialog(false),
    openCreateChartDialog: () => setCreateChartDialog(true),
    addNewRace,
    removeRace,
    moveRace,
    moveParty,
    sortRaces,
    cancel,
    saveChart,
    createAnotherChart,
    finishAndInvoice,
    addPoliticalParty,
    changeRaceType,
    addAllPartisanRaces,
  }
}

export default useManagePartisanChart
